// Derives a <img srcSet> string from a base image URL (e.g. Company.logoUrl)
// and its widths metadata (e.g. Company.logoImageWidths — see
// server/models/company.js). Naming convention (must match
// server/middleware/imageMiddleware.js's processAndUploadImageResponsive):
// the largest width is the un-suffixed base URL itself; every smaller width
// is "{base without .webp}-{width}.webp", in the same path, same query
// string and fragment as the base.

// Keeps only finite, positive, de-duplicated, ascending widths — protects
// against a malformed/tampered widths array (e.g. from a hand-edited
// record, or a future bug elsewhere) producing garbage candidates like
// "NaNw" or a negative width in the srcSet.
// Case-insensitive so a ".WEBP"/".WebP" src still derives correctly — the
// server always generates lowercase variant filenames (see
// server/middleware/imageMiddleware.js), so the replacement target below
// stays lowercase regardless of the base URL's casing.
const WEBP_EXTENSION_RE = /\.webp$/i;

const sanitizeWidths = (widths) => {
  if (!Array.isArray(widths)) return [];
  const cleaned = widths
    .map((w) => Math.round(Number(w)))
    .filter((w) => Number.isFinite(w) && w > 0);
  return Array.from(new Set(cleaned)).sort((a, b) => a - b);
};

/**
 * @param {string} src - the base (un-suffixed) image URL.
 * @param {number[]} widths - e.g. company.logoImageWidths.
 * @returns {string|undefined} a srcSet string, or undefined when there's
 *   nothing usable to build one from (caller should omit the srcSet
 *   attribute entirely in that case, not render an empty/broken one).
 */
export function buildSrcSet(src, widths) {
  if (!src) return undefined;
  const sanitized = sanitizeWidths(widths);
  if (sanitized.length === 0) return undefined;

  const maxWidth = sanitized[sanitized.length - 1];

  let baseUrl;
  try {
    // A base of window.location.origin lets this tolerate a root-relative
    // src (e.g. a bundled fallback asset path) without throwing — though in
    // practice widths are only ever populated alongside a full CDN URL.
    baseUrl = new URL(src, typeof window !== "undefined" ? window.location.origin : undefined);
  } catch {
    return undefined; // not a URL we can safely rewrite — degrade to plain src
  }

  const candidates = sanitized
    .map((width) => {
      if (width === maxWidth) return `${src} ${width}w`;
      if (!WEBP_EXTENSION_RE.test(baseUrl.pathname)) return null;
      const variantUrl = new URL(baseUrl);
      variantUrl.pathname = baseUrl.pathname.replace(WEBP_EXTENSION_RE, `-${width}.webp`);
      return `${variantUrl.toString()} ${width}w`;
    })
    .filter(Boolean);

  return candidates.length > 0 ? candidates.join(", ") : undefined;
}
